import {
    useQuery,
    useInfiniteQuery,
    keepPreviousData
} from '@tanstack/react-query';
import { AnalyticsAPI } from '@newo/api/gateway';
import type { ILogsParams } from '@newo/api/gateway';
import axios from '@/utils/api/axiosInstance';
import { processDateParams } from '@/modules/DateTime/utils';

const analyticsAPI = new AnalyticsAPI(axios);

type IQueryKeys = {
    getLogs: (params?: ILogsParams) => [string] | [string, ILogsParams];
};

export const QUERY_KEYS: IQueryKeys = {
    getLogs: (params) => (params ? ['logs', params] : ['logs'])
};

export const useLogsQuery = (params: ILogsParams, isEnabled?: boolean) => {
    return useQuery({
        queryKey: QUERY_KEYS.getLogs(params),
        queryFn: async () => {
            const processedParams = processDateParams(params);
            return analyticsAPI.getLogs(processedParams);
        },
        placeholderData: keepPreviousData,
        enabled: isEnabled
    });
};

export const usePaginatedLogsQuery = (
    params: ILogsParams,
    isEnabled?: boolean
) => {
    return useInfiniteQuery({
        queryKey: ['analytics/logs/paginated', params],
        queryFn: async ({ pageParam }) => {
            const processedParams = processDateParams(params);
            return analyticsAPI.getLogs({
                ...processedParams,
                cursor: pageParam
            });
        },
        // Cursor-based, not page-based: computing an exact total for the
        // page-based mode forces the server to scan and sort every matching
        // row up front, which scales with the customer's log volume rather
        // than page size and can exhaust server memory on a wide range or a
        // high-volume customer (CDO-178). The cursor mode's cost depends
        // only on page size.
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) =>
            lastPage.metadata.next_cursor ?? undefined,
        gcTime: 0,
        staleTime: 0,
        enabled: isEnabled,
        refetchOnWindowFocus: false
    });
};
